/* Web Push (VAPID) — helper client.
   Gestisce: verifica supporto, registrazione service worker, richiesta permesso,
   subscribe/unsubscribe e sincronizzazione con il backend. Best-effort, mai bloccante. */
import { api } from '@/lib/api';

export function pushSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) return existing;
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch (e) {
    return null;
  }
}

export async function getPushStatus() {
  if (!pushSupported()) return { supported: false, subscribed: false, permission: 'unsupported' };
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return { supported: true, subscribed: !!sub, permission: Notification.permission };
  } catch (e) {
    return { supported: true, subscribed: false, permission: Notification.permission };
  }
}

export async function enablePush() {
  if (!pushSupported()) throw new Error('Il browser non supporta le notifiche push');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    const err = new Error('Permesso notifiche negato');
    err.code = 'denied';
    throw err;
  }
  const reg = await registerServiceWorker();
  if (!reg) throw new Error('Impossibile registrare il service worker');
  await navigator.serviceWorker.ready;
  const { data } = await api.get('/push/vapid-public-key');
  const appServerKey = urlBase64ToUint8Array(data.public_key);
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: appServerKey,
    });
  }
  await api.post('/push/subscribe', { subscription: sub.toJSON(), user_agent: navigator.userAgent });
  return true;
}

export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      try { await api.post('/push/unsubscribe', { subscription: sub.toJSON() }); } catch (e) { /* noop */ }
      try { await sub.unsubscribe(); } catch (e) { /* noop */ }
    }
  } catch (e) { /* noop */ }
  return true;
}

export async function sendTestPush() {
  try { await api.post('/push/test'); return true; } catch (e) { return false; }
}
